/**
 * Figma `57:2`'s credits strip under an administered organisation's header:
 * balance and 7-day consumption, both read straight off `useMe()`'s own
 * `organisations[]` row (`owner-decisions-align-57-2-03`) -- no second query
 * per card.
 *
 * `balance_credits`/`consumed_credits_7d` are withheld together: a guard
 * keyed on `null` alone misses a field a fixture never set (`undefined`),
 * so this checks both loosely (`== null`) and renders the whole strip or
 * nothing -- never a balance with no consumption to compare it against, or
 * a dash. The runway hint stays independent: it has its own "nothing
 * consumed yet" wording rather than a number when consumption is zero.
 */
import type { ReactNode } from 'react';

import { getLocale } from '../../../i18n';
import { compact } from '../../../i18n/format';
import type { OrganisationEntry } from '../session';
import { sw, switcherCatalogue } from './messages';
import { isOrgAdmin } from './role';

const WEEK_MS = 7 * 24 * 60 * 60 * 1000;

/** `runway_until` is the deadline the balance projects to, not a week
 *  count: the frame's own "~5 semanas" is this client's rounding, off
 *  `Date.now()`, not a figure the server sends. */
function weeksUntil(iso: string): number {
  return Math.round((new Date(iso).getTime() - Date.now()) / WEEK_MS);
}

export function OrganisationFigures(props: { organisation: OrganisationEntry }): ReactNode {
  const { organisation } = props;

  // Billing is an organisation administrator's alone; the server withholds it
  // from everyone else, and this holds the line if a response ever carries it.
  if (
    !isOrgAdmin(organisation) ||
    organisation.balance_credits == null ||
    organisation.consumed_credits_7d == null
  ) {
    return null;
  }

  const locale = getLocale();
  const consuming = organisation.consumed_credits_7d > 0;
  const runwayWeeks =
    organisation.runway_until == null ? null : weeksUntil(organisation.runway_until);
  const runwayHint = !consuming
    ? sw('figures.runway.none')
    : runwayWeeks != null
      ? switcherCatalogue.plural('figures.runway.weeks.other', runwayWeeks, { n: runwayWeeks })
      : undefined;

  // Two compact stats in one strip: a full `StatCard` pair here outweighed the
  // organisation's own campaign rows beneath it.
  return (
    <dl className="grid grid-cols-2 gap-4 border-b bg-muted/40 px-4 py-3">
      <div className="flex flex-col gap-0.5">
        <dt className="text-caption text-muted-foreground">{sw('figures.balance')}</dt>
        <dd data-numeric className="text-title-sm">
          {compact(organisation.balance_credits, locale)}
        </dd>
        {runwayHint !== undefined && (
          <dd className="text-caption text-muted-foreground">{runwayHint}</dd>
        )}
      </div>
      <div className="flex flex-col gap-0.5">
        <dt className="text-caption text-muted-foreground">{sw('figures.consumption')}</dt>
        <dd data-numeric className="text-title-sm">
          {consuming
            ? compact(organisation.consumed_credits_7d, locale)
            : sw('figures.consumption.none')}
        </dd>
      </div>
    </dl>
  );
}
